import React from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius, getFieldBorderColor } from '../../../theme/theme';

export interface WorkspaceSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  startIcon?: React.ReactNode;
  endAdornment?: React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
  dataTestId?: string;
  sx?: SxProps<Theme>;
}

export default function WorkspaceSearchField({
  value,
  onChange,
  placeholder,
  ariaLabel,
  startIcon,
  endAdornment,
  inputRef,
  dataTestId,
  sx,
}: WorkspaceSearchFieldProps) {
  const theme = useTheme();
  // Field outlines use the app-wide control border, not the fainter section
  // border — the border-consistency e2e asserts one treatment across surfaces.
  const fieldBorderColor = getFieldBorderColor(theme);

  return (
    <TextField
      size="small"
      fullWidth
      value={value}
      placeholder={placeholder}
      inputRef={inputRef}
      inputProps={{ 'aria-label': ariaLabel }}
      InputProps={{
        startAdornment: startIcon ? (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ) : undefined,
        endAdornment: endAdornment ? (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ) : undefined,
      }}
      onChange={(event) => onChange(event.target.value)}
      data-testid={dataTestId}
      sx={[
        {
          '& .MuiOutlinedInput-root': {
            minHeight: 36,
            borderRadius: `${borderRadius.sm}px`,
            backgroundColor: alpha(
              theme.palette.background.paper,
              theme.palette.mode === 'light' ? 0.92 : 0.3
            ),
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: fieldBorderColor,
            borderWidth: 1,
          },
          '& .MuiOutlinedInput-input': {
            py: 0.65,
            fontSize: '0.875rem',
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
